// @ts-nocheck
import { Area } from 'react-easy-crop';

const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.addEventListener('load', () => resolve(image));
        image.addEventListener('error', (error) => reject(error));
        image.setAttribute('crossOrigin', 'anonymous');
        image.src = url;
    });

export interface CutoutOptions {
    bgThreshold?: number; // 0 to 100 threshold for auto color key removal
    keyColor?: { r: number, g: number, b: number } | null;
    lassoPoints?: { x: number, y: number }[]; // Normalised (0..1) polygon coordinates
    maskImageData?: ImageData | null; // Alpha mask layer from brush/eraser
}

export const getCroppedImg = async (
    imageSrc: string,
    pixelCrop: Area,
    options?: CutoutOptions,
    onProgress?: (progress: number) => void
): Promise<Blob | null> => {
    onProgress?.(10);
    const image = await createImage(imageSrc);

    onProgress?.(30);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    canvas.width = Math.max(1, pixelCrop.width);
    canvas.height = Math.max(1, pixelCrop.height);

    // Render original image onto crop canvas (with transparent padding for out-of-bounds crop)
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
    );

    onProgress?.(50);
    // Apply Lasso/Polygon cutout if points are provided
    if (options?.lassoPoints && options.lassoPoints.length > 2) {
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = canvas.width;
        tempCanvas.height = canvas.height;
        const tempCtx = tempCanvas.getContext('2d');
        if (tempCtx) {
            tempCtx.beginPath();
            options.lassoPoints.forEach((pt, idx) => {
                const px = pt.x * canvas.width;
                const py = pt.y * canvas.height;
                if (idx === 0) tempCtx.moveTo(px, py);
                else tempCtx.lineTo(px, py);
            });
            tempCtx.closePath();
            tempCtx.fillStyle = '#ffffff';
            tempCtx.fill();

            // Mask canvas with polygon
            ctx.globalCompositeOperation = 'destination-in';
            ctx.drawImage(tempCanvas, 0, 0);
            ctx.globalCompositeOperation = 'source-over';
        }
    }

    onProgress?.(70);
    // True AI Neural Background Removal (Instagram / Apple Cutout Quality)
    if (options?.bgThreshold !== undefined && options.bgThreshold > 0) {
        try {
            const { removeBackground } = await import('@imgly/background-removal');
            const resultBlob = await removeBackground(image, {
                publicPath: 'https://static.img.ly/background-removal-data/1.7.0/dist/',
                progress: (key, current, total) => {
                    if (total > 0 && onProgress) {
                        const pct = Math.min(95, Math.floor(70 + (current / total) * 25));
                        onProgress(pct);
                    }
                }
            });
            const cutoutImg = await new Promise<HTMLImageElement>((resolve, reject) => {
                const img = new Image();
                img.crossOrigin = 'anonymous';
                img.onload = () => resolve(img);
                img.onerror = reject;
                img.src = URL.createObjectURL(resultBlob);
            });

            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(
                cutoutImg,
                pixelCrop.x,
                pixelCrop.y,
                pixelCrop.width,
                pixelCrop.height,
                0,
                0,
                pixelCrop.width,
                pixelCrop.height
            );

            // Guarantee 100% solid opacity for all cutout pixels
            const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const data = imgData.data;
            for (let i = 0; i < data.length; i += 4) {
                if (data[i + 3] > 10) {
                    data[i + 3] = 255;
                } else {
                    data[i + 3] = 0;
                }
            }
            ctx.putImageData(imgData, 0, 0);
        } catch (err) {
            console.warn('[imageUtils] AI Background removal failed, executing canvas edge fallback:', err);
            const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const data = imgData.data;
            const width = canvas.width;
            const height = canvas.height;

            const bgSamples: { r: number, g: number, b: number }[] = [];
            for (let x = 0; x < width; x += Math.max(1, Math.floor(width / 15))) {
                const topIdx = x * 4;
                const botIdx = ((height - 1) * width + x) * 4;
                bgSamples.push({ r: data[topIdx], g: data[topIdx + 1], b: data[topIdx + 2] });
                bgSamples.push({ r: data[botIdx], g: data[botIdx + 1], b: data[botIdx + 2] });
            }

            const thresholdSq = Math.pow(65, 2);
            for (let i = 0; i < data.length; i += 4) {
                const r = data[i];
                const g = data[i + 1];
                const b = data[i + 2];
                let isBg = false;
                for (const bg of bgSamples) {
                    const dr = r - bg.r;
                    const dg = g - bg.g;
                    const db = b - bg.b;
                    if (dr * dr + dg * dg + db * db < thresholdSq) {
                        isBg = true;
                        break;
                    }
                }
                if (isBg) {
                    data[i + 3] = 0;
                } else {
                    data[i + 3] = 255;
                }
            }
            ctx.putImageData(imgData, 0, 0);
        }
    }

    onProgress?.(85);
    // Apply Custom Brush Eraser/Restoration mask if present
    if (options?.maskImageData) {
        const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imgData.data;
        const maskCanvas = document.createElement('canvas');
        maskCanvas.width = options.maskImageData.width;
        maskCanvas.height = options.maskImageData.height;
        const maskCtx = maskCanvas.getContext('2d');
        if (maskCtx) {
            maskCtx.putImageData(options.maskImageData, 0, 0);
            const scaledMaskCanvas = document.createElement('canvas');
            scaledMaskCanvas.width = canvas.width;
            scaledMaskCanvas.height = canvas.height;
            const scaledCtx = scaledMaskCanvas.getContext('2d');
            if (scaledCtx) {
                scaledCtx.drawImage(maskCanvas, 0, 0, canvas.width, canvas.height);
                const scaledMaskData = scaledCtx.getImageData(0, 0, canvas.width, canvas.height).data;
                for (let i = 0; i < data.length; i += 4) {
                    // Erased pixels marked in mask canvas (alpha > 0 in red mask)
                    if (scaledMaskData[i + 3] > 0) {
                        data[i + 3] = 0; // Set transparent
                    } else if (data[i + 3] > 0) {
                        data[i + 3] = 255; // 100% Opaque subject
                    }
                }
            }
        }
        ctx.putImageData(imgData, 0, 0);
    }

    onProgress?.(95);
    return new Promise((resolve) => {
        canvas.toBlob((blob) => {
            onProgress?.(100);
            resolve(blob);
        }, 'image/png');
    });
};
